import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import './PostpartumSection.css';

function PostpartumRecovery() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [postpartumInfo, setPostpartumInfo] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [infoData, profileData] = await Promise.all([
        api.getPostpartumInfo(),
        api.getPregnancyProfile(userId).catch(() => null)
      ]);
      setPostpartumInfo(infoData);
      setProfile(profileData);
    } catch (error) {
      console.error('Error loading postpartum recovery:', error);
      setError(t('postpartum.error.load', 'Unable to load postpartum data. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    loadData();
  }, [userId, loadData]);

  if (loading) {
    return (
      <div className="postpartum-section">
        <LoadingSpinner message={t('postpartum.loading.recovery', 'Loading recovery information...')} />
      </div>
    );
  }

  return (
    <div className="postpartum-section">
      <div className="section-header">
        <button className="btn-back" onClick={() => navigate(`/postpartum/${userId}`)}>
          ← {t('action.back', 'Back')}
        </button>
        <h1>💪 {t('postpartum.title.recovery', 'Postpartum Recovery')}</h1>
        <nav className="postpartum-nav">
          <Link to={`/postpartum/${userId}/overview`} className="nav-link">{t('postpartum.nav.overview', 'Overview')}</Link>
          <Link to={`/postpartum/${userId}/recovery`} className="nav-link active">{t('postpartum.nav.recovery', 'Recovery')}</Link>
          <Link to={`/postpartum/${userId}/nutrition`} className="nav-link">{t('postpartum.nav.nutrition', 'Nutrition')}</Link>
          <Link to={`/postpartum/${userId}/appointments`} className="nav-link">{t('postpartum.nav.appointments', 'Appointments')}</Link>
          <Link to={`/postpartum/${userId}/warnings`} className="nav-link">{t('postpartum.nav.warnings', 'Warnings')}</Link>
        </nav>
      </div>

      <div className="section-content">
        {error && <div className="postpartum-error">{error}</div>}
        {postpartumInfo?.physicalRecovery && (
          <>
            {postpartumInfo.physicalRecovery.immediate && (
              <section className="data-section immediate-recovery-section">
                <h2>⚡ {t('postpartum.immediate.recovery', 'Immediate Recovery (0-24 hours)')}</h2>
                <div className="nutrient-foods-grid">
                  {postpartumInfo.physicalRecovery.immediate.bleeding && (
                    <div className="nutrient-category">
                      <h3>🩸 {t('postpartum.bleeding', 'Bleeding (Lochia)')}</h3>
                      <div style={{marginBottom: '15px'}}>
                        <strong style={{display: 'block', marginBottom: '8px', color: '#1976d2'}}>Normal:</strong>
                        <ul>
                          {postpartumInfo.physicalRecovery.immediate.bleeding.normal?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <strong style={{display: 'block', marginBottom: '8px', color: '#c62828'}}>Warning Signs:</strong>
                        <ul>
                          {postpartumInfo.physicalRecovery.immediate.bleeding.warning?.map((item, i) => (
                            <li key={i} style={{color: '#c62828'}}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {postpartumInfo.physicalRecovery.immediate.pain && (
                    <div className="nutrient-category">
                      <h3>😣 {t('postpartum.pain', 'Pain Management')}</h3>
                      <div style={{marginBottom: '15px'}}>
                        <strong style={{display: 'block', marginBottom: '8px'}}>Normal:</strong>
                        <ul>
                          {postpartumInfo.physicalRecovery.immediate.pain.normal?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <strong style={{display: 'block', marginBottom: '8px'}}>Management:</strong>
                        <ul>
                          {postpartumInfo.physicalRecovery.immediate.pain.management?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {postpartumInfo.physicalRecovery.immediate.urination && (
                    <div className="nutrient-category">
                      <h3>🚽 {t('postpartum.urination', 'Urination')}</h3>
                      <div style={{marginBottom: '15px'}}>
                        <strong style={{display: 'block', marginBottom: '8px', color: '#1976d2'}}>Normal:</strong>
                        <ul>
                          {postpartumInfo.physicalRecovery.immediate.urination.normal?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <strong style={{display: 'block', marginBottom: '8px', color: '#c62828'}}>Warning Signs:</strong>
                        <ul>
                          {postpartumInfo.physicalRecovery.immediate.urination.warning?.map((item, i) => (
                            <li key={i} style={{color: '#c62828'}}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {postpartumInfo.physicalRecovery.weeks1to6 && (
              <section className="data-section weeks-recovery-section">
                <h2>📅 {t('postpartum.weeks.1to6', 'Recovery: Weeks 1-6')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category">
                    <h3>💪 {t('postpartum.healing', 'Healing')}</h3>
                    <ul>
                      {postpartumInfo.physicalRecovery.weeks1to6.healing?.map((item, i) => {
                        const isCSectionInfo = item.toLowerCase().includes('c-section') || item.toLowerCase().includes('incision');
                        const shouldShow = profile?.deliveryType === 'C_SECTION' || !isCSectionInfo;
                        return shouldShow ? (
                          <li key={i} style={isCSectionInfo && profile?.deliveryType === 'C_SECTION' ? {fontWeight: 'bold', color: '#1976d2'} : {}}>
                            {item}
                          </li>
                        ) : null;
                      })}
                    </ul>
                  </div>
                  <div className="nutrient-category">
                    <h3>🚶 {t('postpartum.activity', 'Activity')}</h3>
                    <ul>
                      {postpartumInfo.physicalRecovery.weeks1to6.activity?.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="nutrient-category">
                    <h3>🏃 {t('postpartum.exercise', 'Exercise')}</h3>
                    <ul>
                      {postpartumInfo.physicalRecovery.weeks1to6.exercise?.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {postpartumInfo?.exercise && (
              <section className="data-section exercise-section">
                <h2>🏃 {t('postpartum.exercise.guide', 'Exercise Guide')}</h2>
                <div className="nutrient-foods-grid">
                  {postpartumInfo.exercise.firstWeek && (
                    <div className="nutrient-category">
                      <h3>📅 {t('postpartum.week.1', 'First Week')}</h3>
                      <ul>
                        {postpartumInfo.exercise.firstWeek.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {postpartumInfo.exercise.weeks2to6 && (
                    <div className="nutrient-category">
                      <h3>📅 {t('postpartum.weeks.2to6', 'Weeks 2-6')}</h3>
                      <ul>
                        {postpartumInfo.exercise.weeks2to6.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {postpartumInfo.exercise.after6Weeks && (
                    <div className="nutrient-category">
                      <h3>📅 {t('postpartum.after.6weeks', 'After 6 Weeks')}</h3>
                      <ul>
                        {postpartumInfo.exercise.after6Weeks.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {postpartumInfo.exercise.pelvicFloor && (
                    <div className="nutrient-category">
                      <h3>💪 {t('postpartum.pelvic.floor', 'Pelvic Floor Exercises')}</h3>
                      <p style={{marginBottom: '10px'}}>
                        <strong>{postpartumInfo.exercise.pelvicFloor.importance}</strong>
                      </p>
                      <div>
                        <strong style={{display: 'block', marginBottom: '8px'}}>How to do Kegels:</strong>
                        <ul>
                          {postpartumInfo.exercise.pelvicFloor.howTo?.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PostpartumRecovery;

